import {
  isAuthError,
  isAuthRetryableFetchError,
  SupabaseClient,
  User,
} from "@supabase/supabase-js";
import { PriceRow, pricesToRow, rowToCachedPrices } from "../../core/account/priceRows";
import { entryToRow, PortfolioRow, rowToEntry } from "../../core/account/rows";
import {
  Account,
  AccountError,
  AccountErrorKind,
  AccountService,
  CachedPrices,
  CardRef,
  LinkEvent,
  PortfolioChange,
  PortfolioStore,
  PriceStore,
  SettingsStore,
} from "../../core/account/types";
import { parseSettings, Settings } from "../../core/settings";
import type { CardLanguage, PortfolioEntry, PriceQuote } from "../../core/types";

// Accounts and saved data on Supabase (specs/11). The client comes from ./supabase.ts.

export function toAccount(user: User): Account {
  const providers: unknown = user.app_metadata?.providers;
  const meta = user.user_metadata ?? {};
  const account: Account = {
    id: user.id,
    email: user.email ?? "",
    method: Array.isArray(providers) && providers.includes("google") ? "google" : "email",
  };
  const name = meta.full_name ?? meta.name;
  const avatarUrl = meta.avatar_url ?? meta.picture;
  if (typeof name === "string" && name) account.name = name;
  if (typeof avatarUrl === "string" && avatarUrl.startsWith("https://")) {
    account.avatarUrl = avatarUrl;
  }
  return account;
}

export function toAccountError(error: unknown): AccountError {
  const message = error instanceof Error ? error.message : String(error);
  if (isAuthRetryableFetchError(error) || error instanceof TypeError) {
    return new AccountError("network", message);
  }
  if (isAuthError(error)) {
    switch (error.code) {
      case "invalid_credentials":
        return new AccountError("invalid-credentials", message);
      case "over_request_rate_limit":
      case "over_email_send_rate_limit":
        return new AccountError("rate-limit", message);
      case "weak_password":
        return new AccountError("weak-password", message);
      case "same_password":
        return new AccountError("same-password", message);
      case "email_not_confirmed":
        return new AccountError("email-not-confirmed", message);
    }
    if (error.status === 429) return new AccountError("rate-limit", message);
  }
  return new AccountError("unknown", message);
}

/** The error in a link Supabase sent the person back with (11 edge cases), if any. */
export function linkErrorKind(
  error: string | null,
  code: string | null,
  description: string | null,
): AccountErrorKind | null {
  if (!error && !code && !description) return null;
  if (code === "otp_expired" || code === "flow_state_expired") return "link-expired";
  if (code === "signup_disabled" || /signups? not allowed/i.test(description ?? "")) {
    return "google-not-invited";
  }
  if (code === "email_not_confirmed") return "email-not-confirmed";
  if (error === "access_denied" || /cancel|denied/i.test(description ?? "")) {
    return "google-cancelled";
  }
  return "google-failed";
}

export class SupabaseAccountService implements AccountService {
  private linkReported = false;

  constructor(
    private client: SupabaseClient,
    private link: { type: string | null; error: AccountErrorKind | null },
    private appUrl: () => string,
  ) {}

  async verify() {
    await checkSession(this.client);
  }

  async current() {
    const { data, error } = await this.client.auth.getSession();
    if (error) throw toAccountError(error);
    return data.session ? toAccount(data.session.user) : null;
  }

  onChange(cb: (account: Account | null, event?: LinkEvent) => void) {
    const { data } = this.client.auth.onAuthStateChange((event, session) => {
      // The link's session may arrive as INITIAL_SESSION or SIGNED_IN, depending on timing.
      let linkEvent: LinkEvent | undefined;
      if (event === "PASSWORD_RECOVERY") linkEvent = "password-reset";
      else if (session && !this.linkReported && this.link.type === "invite") linkEvent = "invite";
      else if (session && !this.linkReported && this.link.type === "recovery") {
        linkEvent = "password-reset";
      }
      if (linkEvent) this.linkReported = true;
      cb(session ? toAccount(session.user) : null, linkEvent);
    });
    return () => data.subscription.unsubscribe();
  }

  linkError() {
    return this.link.error;
  }

  async signIn(email: string, password: string) {
    const { error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw toAccountError(error);
  }

  async signInWithGoogle() {
    // Only the basic openid, email and profile scopes, so Google needs no app review (FR-58).
    const { error } = await this.client.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: this.appUrl(),
        // Always let the person pick an account. Otherwise Google silently reuses the last one,
        // so after "hasn't been invited" there'd be no way to try a different account.
        queryParams: { prompt: "select_account" },
      },
    });
    if (error) throw toAccountError(error);
  }

  async signOut(everywhere = false) {
    // supabase-js ends the session on this device even when the server can't be reached.
    const { error } = await this.client.auth.signOut({ scope: everywhere ? "global" : "local" });
    if (error && everywhere) throw toAccountError(error);
  }

  async requestPasswordReset(email: string) {
    const { error } = await this.client.auth.resetPasswordForEmail(email, {
      redirectTo: this.appUrl(),
    });
    if (error) throw toAccountError(error);
  }

  async setPassword(password: string) {
    const { error } = await this.client.auth.updateUser({ password });
    if (error) throw toAccountError(error);
  }

  async deleteAccount() {
    const { error } = await this.client.rpc("delete_my_account");
    if (error) throw new AccountError("unknown", error.message);
    // The user is gone, so only this device's session is left to remove.
    await this.client.auth.signOut({ scope: "local" });
  }
}

/**
 * After a failed request: when the session is no longer valid (signed out elsewhere, account
 * deleted by the owner), end it here too, which sends the person to sign in (specs/12 FR-88).
 */
async function checkSession(client: SupabaseClient) {
  const { data } = await client.auth.getSession();
  if (!data.session) return;
  const { error } = await client.auth.getUser();
  if (error && isAuthError(error) && !isAuthRetryableFetchError(error)) {
    await client.auth.signOut({ scope: "local" });
  }
}

/** Every column but user_id. */
const ENTRY_COLUMNS =
  "id, card_id, provider, language, card_snapshot, variant, condition, grading_company, grade, quantity, purchase_price, purchase_date, manual_value, notes, added_at, updated_at";

/** PostgREST returns at most 1,000 rows per request. */
const PAGE = 1000;
/** Keeps request bodies, and ids in the URL of a delete, a sensible size. */
const UPSERT_CHUNK = 500;
const DELETE_CHUNK = 100;

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export class SupabasePortfolioStore implements PortfolioStore {
  constructor(
    private client: SupabaseClient,
    private userId: string,
  ) {}

  private async fail(error: { message: string }): Promise<never> {
    await checkSession(this.client);
    throw new Error(error.message);
  }

  async load(): Promise<PortfolioEntry[]> {
    const entries: PortfolioEntry[] = [];
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await this.client
        .from("portfolio_entries")
        .select(ENTRY_COLUMNS)
        .order("added_at")
        .order("id")
        .range(from, from + PAGE - 1);
      if (error) return this.fail(error);
      const rows = data as unknown as PortfolioRow[];
      entries.push(...rows.map(rowToEntry));
      if (rows.length < PAGE) return entries;
    }
  }

  async save({ upsert, remove }: PortfolioChange) {
    // Deletes first, so replacing a full portfolio stays under the entry limit (FR-77).
    for (const ids of chunks(remove, DELETE_CHUNK)) {
      const { error } = await this.client.from("portfolio_entries").delete().in("id", ids);
      if (error) return this.fail(error);
    }
    for (const entries of chunks(upsert, UPSERT_CHUNK)) {
      const rows = entries.map((e) => ({ ...entryToRow(e), user_id: this.userId }));
      const { error } = await this.client
        .from("portfolio_entries")
        .upsert(rows, { onConflict: "id" });
      if (error) return this.fail(error);
    }
  }
}

/** Card ids per request: they go in the URL of a load. */
const PRICE_LOAD_CHUNK = 100;

/** The shared price cache, `card_prices` (SPECS D20, 04 FR-22a – FR-22e). */
export class SupabasePriceStore implements PriceStore {
  constructor(private client: SupabaseClient) {}

  async load(cards: CardRef[]): Promise<CachedPrices[]> {
    const ids = new Map<CardLanguage, string[]>();
    for (const { language, cardId } of cards) {
      const list = ids.get(language) ?? [];
      if (!list.includes(cardId)) list.push(cardId);
      ids.set(language, list);
    }
    const out: CachedPrices[] = [];
    for (const [language, cardIds] of Array.from(ids)) {
      for (const chunk of chunks(cardIds, PRICE_LOAD_CHUNK)) {
        const { data, error } = await this.client
          .from("card_prices")
          .select("language, card_id, prices, fetched_at")
          .eq("language", language)
          .in("card_id", chunk);
        if (error) {
          await checkSession(this.client);
          throw new Error(error.message);
        }
        for (const row of data as PriceRow[]) {
          const cached = rowToCachedPrices(row);
          if (cached) out.push(cached);
        }
      }
    }
    return out;
  }

  async save(prices: (CardRef & { prices: PriceQuote[] })[]) {
    for (const chunk of chunks(prices, UPSERT_CHUNK)) {
      const { error } = await this.client
        .from("card_prices")
        .upsert(chunk.map(pricesToRow), { onConflict: "language,card_id" });
      if (error) {
        await checkSession(this.client);
        throw new Error(error.message);
      }
    }
  }
}

export class SupabaseSettingsStore implements SettingsStore {
  constructor(
    private client: SupabaseClient,
    private userId: string,
  ) {}

  async load(): Promise<Settings | null> {
    const { data, error } = await this.client
      .from("user_settings")
      .select("settings")
      .maybeSingle();
    if (error) {
      await checkSession(this.client);
      throw new Error(error.message);
    }
    return data ? parseSettings((data as { settings: unknown }).settings) : null;
  }

  async save(settings: Settings) {
    const { error } = await this.client
      .from("user_settings")
      .upsert({ user_id: this.userId, settings }, { onConflict: "user_id" });
    if (error) {
      await checkSession(this.client);
      throw new Error(error.message);
    }
  }
}
